/**
 * Which pages may be shown to the AI. A page with `ai: never` is excluded, and
 * so is everything under it: the import wizard marks sensitive folders (HR)
 * this way. Trashed pages are never visible either. Checked on this device,
 * before anything is sent.
 */
import type { Id } from '@nexus/core';
import type { PageRecord } from '@nexus/store';

export const AI_PROP = 'ai';

type PageWithProps = PageRecord & { props: Record<string, unknown> };

/** `ai: never` (or `ai: false`) on the page itself. */
export function blocksAi(props: Record<string, unknown>): boolean {
  const value = Object.entries(props).find(([k]) => k.toLowerCase() === AI_PROP)?.[1];
  return value === false || (typeof value === 'string' && /^(never|false|no|nee)$/i.test(value));
}

export interface AiScope {
  /** Live pages the AI may see, by id. */
  visible: Map<Id, PageWithProps>;
  /** Pages hidden from the AI (marked themselves or under a marked page). */
  excluded: Set<Id>;
}

export function aiScope(pages: PageWithProps[]): AiScope {
  const byId = new Map(pages.map((p) => [p.id, p]));
  const excluded = new Set<Id>();
  const decided = new Map<Id, boolean>();
  const isExcluded = (id: Id, seen = new Set<Id>()): boolean => {
    const known = decided.get(id);
    if (known !== undefined) return known;
    const page = byId.get(id);
    // A parent that is not live (trashed) does not hide its children by itself.
    const result =
      !!page &&
      (blocksAi(page.props) ||
        (!!page.parentId && !seen.has(page.parentId) && isExcluded(page.parentId, seen.add(id))));
    decided.set(id, result);
    return result;
  };
  const visible = new Map<Id, PageWithProps>();
  for (const p of pages) {
    if (isExcluded(p.id)) excluded.add(p.id);
    else visible.set(p.id, p);
  }
  return { visible, excluded };
}
